import { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
	BulkDeleteButton,
	Create,
	Edit,
	List,
	SimpleForm,
	TextInput,
	BooleanInput,
	Datagrid,
	TextField,
	FunctionField,
	EditButton,
	Button as RaButton,
	SaveButton,
	Toolbar,
	required,
	useDataProvider,
	useGetList,
	useNotify,
	usePermissions,
	useRecordContext,
	useRefresh,
	useRedirect
} from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import {
	Box,
	Button,
	MenuItem,
	Select,
	Stack,
	Switch,
	Tab,
	Tabs,
	TextField as MuiTextField,
	Typography
} from '@mui/material';
import { default as icon } from '@mui/icons-material/ViewModule';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PromptTextBlock from '../components/PromptTextBlock';

const PromptDraftResetContext = createContext({
	resetRef: { current: () => {} },
	dirty: false,
	setDirty: () => {}
});

const stageLabels = {
	start: 'Start',
	interview: 'Interview',
	summary: 'Summary'
};

function versionStatusLabel( version ) {
	const status = version.status || ( version.active ? 'active' : 'draft' );
	return [ 'active', 'draft' ].includes( status ) ? ` (${status})` : '';
}

const CloneModuleButton = (props) => {
	const record = useRecordContext();
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const redirect = useRedirect();

	const handleClone = async ( event ) => {
		event.stopPropagation();
		try {
			const { data } = await dataProvider.cloneModule({ id: record.id });
			notify( 'Module cloned' );
			redirect( 'edit', 'modules', data.id );
		} catch ( error ) {
			notify( error.message || 'Clone failed', { type: 'error' } );
		}
	};

	return (
		<Button
			{...props}
			variant="outlined"
			onClick={handleClone}
			sx={{ ml: 1 }}
		>
			Clone Module
		</Button>
	);
};

const CancelModuleButton = () => {
	const { reset, formState } = useFormContext();
	const { resetRef, dirty: promptDirty } = useContext( PromptDraftResetContext );

	const handleCancel = () => {
		reset();
		resetRef.current();
	};

	return (
		<Button
			variant="outlined"
			onClick={handleCancel}
			disabled={!formState.isDirty && !promptDirty}
			sx={{ ml: 1 }}
		>
			Cancel
		</Button>
	);
};

const ModuleToolbar = () => (
	<Toolbar sx={{ gap: 1 }}>
		<SaveButton />
		<CancelModuleButton />
		<CloneModuleButton />
	</Toolbar>
);

const CloneModuleListButton = () => {
	const record = useRecordContext();
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const redirect = useRedirect();

	const handleClone = async ( event ) => {
		event.stopPropagation();
		try {
			const { data } = await dataProvider.cloneModule({ id: record.id });
			notify( 'Module cloned' );
			redirect( 'edit', 'modules', data.id );
		} catch ( error ) {
			notify( error.message || 'Clone failed', { type: 'error' } );
		}
	};

	return (
		<RaButton label="Clone" onClick={handleClone}>
			<ContentCopyIcon />
		</RaButton>
	);
};

const ModuleActiveToggle = ({ record }) => {
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const refresh = useRefresh();
	const [ saving, setSaving ] = useState( false );
	const checked = Number( record?.active ) === 1;

	const handleChange = async ( event ) => {
		event.stopPropagation();
		const active = event.target.checked ? 1 : 0;
		setSaving( true );

		try {
			await dataProvider.update( 'modules', {
				id: record.id,
				data: { ...record, active },
				previousData: record
			});
			notify( active ? 'Module activated' : 'Module deactivated' );
			refresh();
		} catch ( error ) {
			notify( error.message || 'Module update failed', { type: 'error' } );
		} finally {
			setSaving( false );
		}
	};

	return (
		<Switch
			checked={checked}
			disabled={saving}
			onChange={handleChange}
			onClick={event => event.stopPropagation()}
			size="small"
		/>
	);
};

const ModuleList = () =>
	<List>
		<Datagrid rowClick="edit" bulkActionButtons={<BulkDeleteButton mutationMode="pessimistic" />}>
			<TextField source="title" />
			<TextField source="description" />
			<FunctionField
				label="Active"
				render={record => <ModuleActiveToggle record={record} />}
			/>
			<EditButton />
			<CloneModuleListButton />
		</Datagrid>
	</List>;

const ModuleDetailsInputs = (
	<>
		<TextInput source="title" validate={required()} />
		<TextInput source="description" multiline />
		<BooleanInput source="active" defaultValue={true} />
	</>
);

const ReadOnlyPrompt = ({ label, body }) => (
	<PromptTextBlock
		label={label}
		text={body}
		fallback="(No prompt body)"
		maxHeight={520}
		sx={{ mb: 1 }}
	/>
);

const PromptStageEditor = ({ stage }) => {
	const record = useRecordContext();
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const { permissions } = usePermissions();
	const [ versions, setVersions ] = useState([]);
	const [ selectedId, setSelectedId ] = useState('');
	const [ body, setBody ] = useState('');
	const [ preview, setPreview ] = useState(null);
	const [ saving, setSaving ] = useState(false);
	const [ editing, setEditing ] = useState(false);
	const selected = versions.find( version => String( version.id ) === String( selectedId ) );
	const isDraft = selected?.status === 'draft' && Number( selected?.active ) !== 1;
	const isActive = Number( selected?.active ) === 1;
	const canEdit = ( isDraft || isActive ) && permissions?.['prompt-versions']?.includes( 'edit' );
	const isReadOnly = !editing || !canEdit;
	const canActivate = selected && Number( selected.active ) !== 1;
	const canDelete = selected && !isActive && permissions?.['prompt-versions']?.includes( 'delete' );
	const isDirty = !isReadOnly && !!selected && body !== ( selected.body || '' );
	const canViewTemplate = permissions?.['prompt-templates']?.some( action => [ 'show', 'list' ].includes( action ) );
	const canPreview = permissions?.['prompt-preview']?.includes( 'show' );
	const { data: templates = [] } = useGetList(
		'prompt-templates',
		{
			filter: { prompt_key: stage, active: 1 },
			pagination: { page: 1, perPage: 1 },
			sort: { field: 'updatedAt', order: 'DESC' }
		},
		{ enabled: canViewTemplate }
	);
	const activeTemplate = templates[0];
	const canEditTemplate = permissions?.['prompt-templates']?.includes( 'edit' );
	const templateGuidance = activeTemplate?.guidance?.trim() || '';

	const loadVersions = ( preferredId ) => {
		if ( !record?.id ) {
			return Promise.resolve();
		}

		return dataProvider.getList( 'prompt-versions', {
			filter: { module_id: record.id, prompt_key: stage },
			pagination: { page: 1, perPage: 100 },
			sort: { field: 'updatedAt', order: 'DESC' }
		}).then(({ data }) => {
			setVersions( data );
			setEditing( false );
			setPreview( null );
			const next = data.find( version => String( version.id ) === String( preferredId ) )
				|| data.find( version => Number( version.active ) === 1 )
				|| data[0];
			if ( next ) {
				setSelectedId( next.id );
				setBody( next.body || '' );
			} else {
				setSelectedId( '' );
				setBody( '' );
			}
		});
	};

	useEffect(() => {
		loadVersions().catch(error => notify( error.message || 'Could not load prompt versions', { type: 'error' } ));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ record?.id, stage ]);

	const selectVersion = event => {
		if ( isDirty && !window.confirm( 'You have unsaved changes to this prompt version. Discard them and switch versions?' ) ) {
			return;
		}
		const id = event.target.value;
		const version = versions.find( item => String( item.id ) === String( id ) );
		setSelectedId( id );
		setEditing( false );
		setPreview( null );
		setBody( version?.body || '' );
	};

	const cancelEdits = () => {
		if ( !selected ) {
			return;
		}
		setBody( selected.body || '' );
		setEditing( false );
		setPreview( null );
	};

	const { resetRef, setDirty } = useContext( PromptDraftResetContext );
	useEffect(() => {
		resetRef.current = cancelEdits;
		return () => {
			resetRef.current = () => {};
		};
	});

	useEffect(() => {
		setDirty( isDirty );
	}, [ isDirty, setDirty ]);

	const savePrompt = async () => {
		if ( !selected || isReadOnly || saving ) {
			return;
		}

		setSaving( true );
		try {
			await dataProvider.update( 'prompt-versions', {
				id: selected.id,
				data: { body },
				previousData: selected
			});
			notify( isActive ? 'Active prompt saved' : 'Draft saved' );
			await loadVersions( selected.id );
		} catch ( error ) {
			notify( error.message || 'Prompt save failed', { type: 'error' } );
		} finally {
			setSaving( false );
		}
	};

	const deleteVersion = async () => {
		if ( !canDelete || saving || !window.confirm( 'Delete this inactive prompt version? This cannot be undone.' ) ) {
			return;
		}

		setSaving( true );
		try {
			await dataProvider.delete( 'prompt-versions', { id: selected.id, previousData: selected } );
			notify( 'Prompt version deleted' );
			await loadVersions();
		} catch ( error ) {
			notify( error.message || 'Prompt deletion failed', { type: 'error' } );
		} finally {
			setSaving( false );
		}
	};

	const createDraftFromSelected = async () => {
		if ( saving ) {
			return;
		}
		const source = selected;
		setSaving( true );
		try {
			const { data } = await dataProvider.create( 'prompt-versions', {
				data: {
					module_id: record.id,
					prompt_key: stage,
					label: source ? `Draft from ${source.label || `version ${source.id}`}` : 'Draft',
					body: source ? body : '',
					status: 'draft',
					active: 0
				}
			});
			notify( 'Draft created' );
			await loadVersions( data.id );
		} catch ( error ) {
			notify( error.message || 'Draft creation failed', { type: 'error' } );
		} finally {
			setSaving( false );
		}
	};

	const activateVersion = async () => {
		if ( !canActivate || saving ) {
			return;
		}

		setSaving( true );
		try {
			await dataProvider.update( 'prompt-versions', {
				id: selected.id,
				data: { ...selected, body, status: 'active', active: 1 },
				previousData: selected
			});
			notify( 'Prompt version activated' );
			await loadVersions( selected.id );
		} catch ( error ) {
			notify( error.message || 'Activation failed', { type: 'error' } );
		} finally {
			setSaving( false );
		}
	};

	const previewPrompt = async () => {
		try {
			const { data } = await dataProvider.previewPrompt({
				module_preview: true,
				module_id: record.id,
				stage,
				prompt_body: body
			});
			setPreview( data );
		} catch ( error ) {
			notify( error.message || 'Preview failed', { type: 'error' } );
		}
	};

	return (
		<Box component="fieldset" disabled={saving} sx={{ width: '100%', mt: 2, p: 0, border: 0, minWidth: 0 }}>
			<Stack spacing={1} alignItems="flex-start">
				<Select
					size="small"
					value={selectedId}
					onChange={selectVersion}
					disabled={saving}
					displayEmpty
					inputProps={{ 'aria-label': 'Prompt version' }}
					sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: '100%', sm: 'auto' }, maxWidth: '100%' }}
				>
					<MenuItem value="">Select version</MenuItem>
					{versions.map(version => (
						<MenuItem key={version.id} value={version.id}>
							{version.label || `Version ${version.id}`}{versionStatusLabel( version )}
						</MenuItem>
					))}
				</Select>
				<Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
					{canEdit && isReadOnly && <Button size="small" variant="contained" onClick={() => setEditing( true )}>Edit</Button>}
					{!isReadOnly && (
						<>
							<Button size="small" variant="contained" onClick={savePrompt} disabled={!isDirty}>{isActive ? 'Save Prompt' : 'Save Draft'}</Button>
							<Button size="small" variant="outlined" onClick={cancelEdits}>Cancel</Button>
						</>
					)}
					<Button size="small" variant="outlined" onClick={createDraftFromSelected} disabled={!record?.id}>{selected ? 'Copy to New' : 'Create Draft'}</Button>
					{canDelete && <Button size="small" color="error" variant="outlined" onClick={deleteVersion}>Delete</Button>}
				</Stack>
			</Stack>
			<Stack direction="row" alignItems="center" sx={{ gap: 1, flexWrap: 'wrap', mt: 1, mb: 2 }}>
				{canPreview && <Button size="small" variant="outlined" onClick={previewPrompt} disabled={!record?.id}>Preview</Button>}
				{canViewTemplate && (
					<Button
						size="small"
						variant="outlined"
						component={RouterLink}
						to={activeTemplate ? `/prompt-templates/${activeTemplate.id}${canEditTemplate ? '' : '/show'}` : '/prompt-templates'}
					>
						{canEditTemplate ? 'Edit' : 'View'} {stageLabels[stage]} Template
					</Button>
				)}
				{canActivate && <Button size="small" variant="outlined" onClick={activateVersion}>Activate Version</Button>}
			</Stack>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: templateGuidance
						? { xs: '1fr', md: 'minmax(0, 2fr) minmax(280px, 1fr)' }
						: '1fr',
					gap: 2
				}}
			>
				{isReadOnly ? (
					<ReadOnlyPrompt label={`${stageLabels[stage]} Prompt`} body={body} />
				) : (
					<MuiTextField
						label={`${stageLabels[stage]} Prompt`}
						value={body}
						onChange={event => setBody( event.target.value )}
						multiline
						minRows={18}
						fullWidth
					/>
				)}
				{templateGuidance && <ReadOnlyPrompt label="Module prompt guidance" body={templateGuidance} />}
			</Box>
			{preview && (
				<Box sx={{ mt: 2 }}>
					<ReadOnlyPrompt label="Prompt Preview" body={preview.prompt} />
				</Box>
			)}
		</Box>
	);
};

const ModulePromptTabs = () => {
	const [ stage, setStage ] = useState( 'start' );
	const { dirty } = useContext( PromptDraftResetContext );
	const selectStage = ( event, value ) => {
		if ( dirty && !window.confirm( 'You have unsaved changes to this prompt version. Discard them and switch stages?' ) ) {
			return;
		}
		setStage( value );
	};
	return (
		<Box sx={{ width: '100%', mt: 2 }}>
			<Typography variant="h6">Prompts</Typography>
			<Tabs value={stage} onChange={selectStage}>
				<Tab label="Start" value="start" />
				<Tab label="Interview" value="interview" />
				<Tab label="Summary" value="summary" />
			</Tabs>
			<PromptStageEditor stage={stage} />
		</Box>
	);
};

const ModuleEdit = () => {
	const resetRef = useRef(() => {});
	const [ promptDirty, setPromptDirty ] = useState(false);

	return (
		<Edit redirect="edit">
			<PromptDraftResetContext.Provider value={{ resetRef, dirty: promptDirty, setDirty: setPromptDirty }}>
				<SimpleForm toolbar={<ModuleToolbar />} warnWhenUnsavedChanges>
					{ModuleDetailsInputs}
					<ModulePromptTabs />
				</SimpleForm>
			</PromptDraftResetContext.Provider>
		</Edit>
	);
};

const ModuleCreate = () => (
	<Create>
		<SimpleForm>
			{ModuleDetailsInputs}
		</SimpleForm>
	</Create>
);

export const Module = { list: ModuleList, edit: ModuleEdit, create: ModuleCreate, icon };

export default Module;
